import { useCallback } from 'react';

import { type MutationResult, useMutation } from '@apollo/client/react';

import {
  type Lead_Agents_Bool_Exp,
  type Lead_Agents_Set_Input,
  type UpdateLeadAgentsMutation,
  type UpdateLeadAgentsMutationVariables,
} from '@graphql/__generated__/graphql';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { UPDATE_LEAD_AGENTS } from '@modules/leads/leadsQueries';

export const useUpdateLeadAgents = (
  options?: Parameters<
    typeof useMutation<
      UpdateLeadAgentsMutation,
      UpdateLeadAgentsMutationVariables
    >
  >[1],
) => {
  const [updateLeadAgent, mutationResult] = useMutation(
    UPDATE_LEAD_AGENTS,
    options,
  );
  const { me } = useAppData();

  const memoizedUpdate = useCallback(
    (leadId: string, set: Lead_Agents_Set_Input, agentId?: string) => {
      // Will update all the lead_agents of a team for a given lead to prevent having different stages within a team.
      // Fallback to all lead_agents not coming from the valuation result page in case no agent is provided for admins.
      const teamScopedWhereClause: Lead_Agents_Bool_Exp =
        agentId != null
          ? {
              user: {
                teams_users: {
                  team: { teams_users: { user_id: { _eq: agentId } } },
                },
              },
            }
          : {};

      // For non-admins, only touch active (non-passed) lead_agents so we don't
      // accidentally revive passed ones when updating status/qualification.
      const nonAdminWhereClause: Lead_Agents_Bool_Exp = {
        passed_at: { _is_null: true },
        ...teamScopedWhereClause,
      };

      return updateLeadAgent({
        variables: {
          where: {
            lead_id: { _eq: leadId },
            ...(me?.is_admin === true
              ? teamScopedWhereClause
              : nonAdminWhereClause),
          },
          set,
        },
      }).then(result => {
        emitFeedRefresh();
        return result;
      });
    },
    [updateLeadAgent, me],
  );

  return [memoizedUpdate, mutationResult] as [
    mutate: typeof memoizedUpdate,
    result: MutationResult<UpdateLeadAgentsMutation>,
  ];
};
